import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit, Renderer2, ElementRef, ViewChild, ChangeDetectorRef, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';

import {MatButton, MatIconButton} from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog'; // Ensure MatDialogModule is imported
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DataService, YearwiseResponse, Exam, Paper, Year } from '../../../services/data.service';
import { finalize } from 'rxjs/operators';
import { NavigationLayoutDirective } from './navigation-layout.directive';
// Import Tree related modules
import { MatTreeModule } from '@angular/material/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { BehaviorSubject, Subject, takeUntil, take } from 'rxjs';
import { NgZone } from '@angular/core';
// Import FontSizeService
import { FontSizeService, FontSizeOption } from '../../../services/font-size.service';
import { NavigationPathService, NavigationPathItem } from '../../../services/navigation-path.service';
import { RoleService } from '../../../services/role.service';
import { TreeEditService } from '../../../services/tree-edit.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
// Import TreeService
import { TreeService } from '../../../services/tree.service';
// Import dialog components
import { TopicDialogComponent } from '../../dialogs/topic-dialog/topic-dialog.component';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';

// Define the node interface for the tree
export interface SubjectNode {
  name: string;
  hindiName?: string;
  children?: SubjectNode[];
  type: 'subject' | 'section';
  hasChildren?: boolean;
  level?: number;
  topicId?: string;
}

// Define the node interface for the yearwise tree
export interface YearwiseNode {
  name: string;
  hindiName?: string;
  children?: YearwiseNode[];
  type: 'exam' | 'paper';
}

// Define the node interface for the topics tree in pane2
export interface TopicNode {
  name: string;
  hindiName?: string;
  children?: TopicNode[];
  type: 'topic' | 'subtopic';
  level?: number;
  hasChildren?: boolean;
  topicId?: string;
}

@Component({
  selector: 'app-navigation-app',
  standalone: true,
  imports: [
    NgIf, NgForOf, NgSwitch, NgSwitchCase,
    MatToolbar, MatButton,
    MatTooltipModule, ScrollingModule, MatProgressSpinnerModule,
    NavigationLayoutDirective, MatTreeModule,
    MatIcon, MatMenu, MatMenuItem, MatMenuTrigger,
    MatIconButton, MatSlideToggle, FormsModule,
    MatDialogModule
  ],
  templateUrl: './navigation-app.component.html',
  styleUrls: ['./navigation-app.component.css']
})
export class NavigationAppComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  // Navigation state
  isCollapsed = false;
  isPaneTwo_Collapsed = false;
  isPaneOne_Collapsed = false;

  treeControl = new NestedTreeControl<SubjectNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<SubjectNode>();
  treeData: SubjectNode[] = [];
  isTreeExpanded = false;

  yearwiseTreeControl = new NestedTreeControl<YearwiseNode>(node => node.children);
  yearwiseDataSource = new MatTreeNestedDataSource<YearwiseNode>();
  yearwiseTreeData: YearwiseNode[] = [];
  isYearwiseTreeExpanded = false;

  topicTreeControl = new NestedTreeControl<TopicNode>(node => node.children);
  topicDataSource = new MatTreeNestedDataSource<TopicNode>();
  topicTreeData: TopicNode[] = [];
  isTopicTreeExpanded = false;

  // Hindi name mapping received from API
  hindiNameMap: {[key: string]: string} = {};

  // Language selection
  languageOptions = [
    { value: 'bilingual', label: 'Bilingual' },
    { value: 'englishonly', label: 'English Only' },
    { value: 'hindionly', label: 'Hindi Only' }
  ];
  selectedLanguage: string = 'englishonly'; // Default

  // Add a flag to track when language change is in progress
  isLanguageChangeInProgress = false;

  // Add a debounce timer for language changes
  private languageChangeTimer: any = null;

  // ViewChild references to menu triggers
  @ViewChild('topicwiseMenuTrigger') topicwiseMenuTrigger!: MatMenuTrigger;
  @ViewChild('yearwiseMenuTrigger') yearwiseMenuTrigger!: MatMenuTrigger;
  @ViewChild('topicsMenuTrigger') topicsMenuTrigger!: MatMenuTrigger;

  // Inputs for data and configuration
  @Input() p1Mode: 'topicwise' | 'yearwise' = 'topicwise';
  @Input() p1Subjects: string[] = [];
  @Input() p1SubMap: {[key: string]: string[]} = {};
  @Input() p2TopicsMap: {[key: string]: string[]} = {};
  @Input() p2SubTopicsMap: {[key: string]: string[]} = {};
  @Input() p1YearwiseExams: string[] = [];
  @Input() p1YearwiseLower: {[key: string]: string[]} = {};
  @Input() p2Years: {[key: string]: string[]} = {};
  @Input() selectedSubject: string | null = null;
  @Input() selectedSubItem: string | null = null;
  @Input() selectedTopic: string | null = null;
  @Input() selectedSubTopic: string | null = null;
  @Input() selectedYearwiseExam: string | null = null;
  @Input() selectedYearwiseLower: string | null = null;
  @Input() selectedYear: string | null = null;
  @Input() isLoadingSubjects = false;
  @Input() isLoadingSections = false;
  @Input() isLoadingTopics = false;
  @Input() isLoadingSubtopics = false;
  @Input() topicIdMap: { [topicName: string]: { id: string, level: number, hasChildren: boolean, hindiName?: string } } = {};

  // UI configuration
  @Input() hideToggle: boolean = false;
  @Input() customTitle: string = '';

  // Output events
  @Output() modeChange = new EventEmitter<'topicwise' | 'yearwise'>();
  @Output() subjectClick = new EventEmitter<string>();
  @Output() subItemClick = new EventEmitter<string>();
  @Output() topicClick = new EventEmitter<string>();
  @Output() subTopicClick = new EventEmitter<string>();
  @Output() yearwiseExamClick = new EventEmitter<string>();
  @Output() yearwiseLowerClick = new EventEmitter<string>();
  @Output() yearClick = new EventEmitter<string>();
  @Output() yearsListGenerated = new EventEmitter<string[]>();
  @Output() subjectsLoaded = new EventEmitter<string[]>();
  @Output() sectionsLoaded = new EventEmitter<{subject: string, sections: string[]}>();
  @Output() topicsLoaded = new EventEmitter<{section: string, topics: string[]}>();
  @Output() subtopicsLoaded = new EventEmitter<{topic: string, subtopics: string[]}>();
  @Output() collapseStateChange = new EventEmitter<boolean>();
  @Output() languageChange = new EventEmitter<string>();
  @Output() hindiNamesRequested = new EventEmitter<void>();
  @Output() viewButtonClick = new EventEmitter<void>();
  @Output() settingsButtonClick = new EventEmitter<void>();
  @Output() refreshTreeRequested = new EventEmitter<void>();
  @Output() editModeChange = new EventEmitter<boolean>();

  // Mock data for development
  mockYearwiseExams: string[] = [];
  mockYearwiseLower: {[key: string]: string[]} = {};

  // Internal state
  isSelecting = false;
  private initialLoadComplete = false;

  // Font size options
  fontSizeOptions: { value: FontSizeOption, label: string }[] = [
    { value: 'large', label: 'Large' },
    { value: 'medium', label: 'Medium' },
    { value: 'small', label: 'Small' }
  ];

  // Current font size
  currentFontSize: FontSizeOption = 'medium';

  // New output for font size changes
  @Output() fontSizeChange = new EventEmitter<FontSizeOption>();

  // ViewChild for font size menu
  @ViewChild('fontSizeMenuTrigger') fontSizeMenuTrigger!: MatMenuTrigger;

  // Get current font size from service for menu indicator
  get currentFontSizeFactor(): number {
    return this.fontSizeService.getFontSizeFactor();
  }

  // Edit tree mode
  isEditMode: boolean = false;
  isInternalUser: boolean = false;
  editingNode: any = null;

  // Add destroy Subject for takeUntil operator
  private destroy$ = new Subject<void>();

  constructor(
    private dataService: DataService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private zone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
    private fontSizeService: FontSizeService,
    private navigationPathService: NavigationPathService,
    private roleService: RoleService,
    private treeEditService: TreeEditService,
    private dialog: MatDialog,
    private treeService: TreeService // Add TreeService
  ) {}

  ngOnInit(): void {
    console.log('NavigationAppComponent ngOnInit called');

    // Check user role
    this.checkUserRole();

    // Get font size from service
    this.fontSizeService.fontSize$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((size: FontSizeOption) => {
      this.currentFontSize = size;
    });

    // For real data, check if we have subjects already or need to load the tree data
    if (!this.p1Subjects || this.p1Subjects.length === 0) {
      console.log('No subjects available, loading from TreeService');
      this.treeService.getTreeData().pipe(take(1)).subscribe(treeData => {
        if (treeData && treeData.subjects) {
          console.log('Got subjects from TreeService:', Object.keys(treeData.subjects));

          // Extract subjects from tree data
          this.p1Subjects = Object.keys(treeData.subjects);

          // Extract section mappings
          if (treeData.subjects) {
            Object.keys(treeData.subjects).forEach(subject => {
              if (treeData.subjects[subject].sections) {
                this.p1SubMap[subject] = Object.keys(treeData.subjects[subject].sections);

                // Also extract topics for each section
                Object.keys(treeData.subjects[subject].sections).forEach(section => {
                  if (treeData.subjects[subject].sections[section].topics) {
                    this.p2TopicsMap[section] = Object.keys(treeData.subjects[subject].sections[section].topics);

                    // Extract subtopics for each topic
                    Object.keys(treeData.subjects[subject].sections[section].topics).forEach(topic => {
                      if (treeData.subjects[subject].sections[section].topics[topic].subtopics) {
                        this.p2SubTopicsMap[topic] = Object.keys(
                          treeData.subjects[subject].sections[section].topics[topic].subtopics
                        );
                      }
                    });
                  }
                });
              }
            });
          }

          // Force tree data update
          this.updateTreeData();

          // Expand all nodes to make navigation visible
          setTimeout(() => {
            this.expandAllNodes();
          }, 500);
        } else {
          console.error('Failed to get subject data from TreeService');
        }
      });
    } else {
      // Update tree data with existing subjects
      this.updateTreeData();
    }

    // Return indicator values based on parent app-home-component values
    console.log(`p1Mode from parent: ${this.p1Mode}`);

    // Force tree data update
    setTimeout(() => {
      this.refreshTreeUI();

      // Auto-select initial data if needed
      this.checkInitialSelections();
    }, 1000);

    // For demo yearwise data - this would be replaced by API calls
    if (this.p1YearwiseExams.length === 0) {
      this.fetchYearwiseData();
    }
  }

  // Check and apply initial selections if available from parent
  private checkInitialSelections(): void {
    // If any selections were passed from parent, apply them
    if (this.selectedSubject && this.p1Mode === 'topicwise') {
      console.log(`Initial subject selection: ${this.selectedSubject}`);
      this.expandTreeForNode(this.selectedSubject);

      if (this.selectedSubItem) {
        console.log(`Initial section selection: ${this.selectedSubItem}`);
        if (this.p2TopicsMap[this.selectedSubItem]?.length > 0) {
          this.updateTopicTreeData();
        }
      }
    } else if (this.selectedYearwiseExam && this.p1Mode === 'yearwise') {
      console.log(`Initial yearwise exam selection: ${this.selectedYearwiseExam}`);
      this.updateYearwiseTreeData();
    }
  }

  // Expand the tree for a specific node by name
  private expandTreeForNode(nodeName: string): void {
    if (!this.treeControl.dataNodes) {
      console.warn('Tree control has no data nodes yet');
      return;
    }

    const nodeToExpand = this.treeControl.dataNodes.find(node => node.name === nodeName);
    if (nodeToExpand) {
      console.log(`Expanding tree for node: ${nodeName}`);
      this.treeControl.expand(nodeToExpand);
    } else {
      console.warn(`Node ${nodeName} not found in tree control data nodes`);
    }
  }

  // Helper method to check user role
  private checkUserRole(): void {
    this.isInternalUser = this.roleService.getRole() === 'internal';
    console.log('User role check: isInternalUser =', this.isInternalUser);
  }

  ngAfterViewInit(): void {
    // Refresh tree UI to ensure proper visibility
    this.refreshTreeUI();

    // Initialize tree control dataNodes if needed
    setTimeout(() => {
      if (this.treeData.length > 0 && !this.treeControl.dataNodes) {
        console.log('Initializing tree control dataNodes in ngAfterViewInit');
        this.treeControl.dataNodes = this.treeData;
      }
    }, 100);
  }

  // Make sure tree controls have proper data assigned
  private ensureTreeControlsHaveData(): void {
    // Main tree
    if (!this.treeControl.dataNodes && this.treeData.length > 0) {
      this.treeControl.dataNodes = this.treeData;
    }

    // Yearwise tree
    if (!this.yearwiseTreeControl.dataNodes && this.yearwiseTreeData.length > 0) {
      this.yearwiseTreeControl.dataNodes = this.yearwiseTreeData;
    }

    // Topic tree
    if (!this.topicTreeControl.dataNodes && this.topicTreeData.length > 0) {
      this.topicTreeControl.dataNodes = this.topicTreeData;
    }

    // Only trigger expansion if not already done and we have data
    if (!this.initialLoadComplete && this.treeControl.dataNodes?.length > 0) {
      this.initialLoadComplete = true;
      this.expandFirstNodePath();
    }
  }

  // Method to expand the first subject, its first section, first topic, and first subtopic
  private expandFirstNodePath(): void {
    // First make sure everything is collapsed initially
    this.collapseAllNodes();
    this.collapseAllTopicNodes();

    // 1. Expand first subject
    if (this.treeControl.dataNodes && this.treeControl.dataNodes.length > 0) {
      const firstSubject = this.treeControl.dataNodes[0];
      if (firstSubject) {
        this.treeControl.expand(firstSubject);

        // Select first subject to trigger any data loading
        if (this.selectedSubject !== firstSubject.name) {
          this.selectedSubject = firstSubject.name;
          this.subjectClick.emit(firstSubject.name);
        }

        // 2. If first subject has children (sections), expand and select first section
        if (firstSubject.children && firstSubject.children.length > 0) {
          const firstSection = firstSubject.children[0];
          if (firstSection) {
            // Select first section to trigger topic loading
            if (this.selectedSubItem !== firstSection.name) {
              this.selectedSubItem = firstSection.name;
              this.subItemClick.emit(firstSection.name);
            }

            // Wait for topics to load before expanding them
            setTimeout(() => {
              // 3. Expand first topic in the selected section
              if (this.topicTreeControl.dataNodes && this.topicTreeControl.dataNodes.length > 0) {
                const firstTopic = this.topicTreeControl.dataNodes[0];
                if (firstTopic) {
                  this.topicTreeControl.expand(firstTopic);

                  // Select first topic to trigger subtopics loading
                  if (this.selectedTopic !== firstTopic.name) {
                    this.selectedTopic = firstTopic.name;
                    this.topicClick.emit(firstTopic.name);
                  }

                  // 4. If first topic has children (subtopics), expand first subtopic
                  if (firstTopic.children && firstTopic.children.length > 0) {
                    const firstSubtopic = firstTopic.children[0];
                    if (firstSubtopic) {
                      this.topicTreeControl.expand(firstSubtopic);

                      // Optionally select the first subtopic
                      if (this.selectedSubTopic !== firstSubtopic.name) {
                        this.selectedSubTopic = firstSubtopic.name;
                        this.subTopicClick.emit(firstSubtopic.name);
                      }
                    }
                  }
                }
              }
            }, 500);
          }
        }
      }
    }
  }

  // Tree utility methods
  hasChild = (_: number, node: SubjectNode) => {
    // Check if the node has visible children in the tree
    const hasVisibleChildren = !!node.children && node.children.length > 0;

    // Also check the hasChildren flag which may be set based on API metadata
    const hasChildrenFlag = node.hasChildren !== undefined ? node.hasChildren : false;

    // Add logging for debug
    if (node.level && node.level >= 2) {
      console.log(`Checking hasChild for ${node.name} (level ${node.level}): visible children=${hasVisibleChildren}, hasChildren flag=${hasChildrenFlag}`);
    }

    // Show toggle if node has visible children or hasChildren flag is set
    return hasVisibleChildren || hasChildrenFlag;
  };
  hasYearwiseChild = (_: number, node: YearwiseNode) => !!node.children && node.children.length > 0;
  hasTopicChild = (_: number, node: TopicNode) => {
    // Check if the node has visible children in the tree
    const hasVisibleChildren = !!node.children && node.children.length > 0;

    // Check the hasChildren flag from metadata
    const hasChildrenFlag = !!node.hasChildren;

    // We'll show the toggle if:
    // 1. The node actually has visible children, OR
    // 2. The node is explicitly marked as having children (from metadata)
    //    but no children are loaded yet
    const shouldShowToggle = hasVisibleChildren || (hasChildrenFlag && (!node.children || node.children.length === 0));

    return shouldShowToggle;
  };

  // Update the subject tree data
  updateTreeData(): void {
    console.log('NavigationApp: updateTreeData called');
    console.log('Input data counts:', {
      subjects: this.p1Subjects?.length || 0,
      p1SubMap: Object.keys(this.p1SubMap || {}).length,
      p2TopicsMap: Object.keys(this.p2TopicsMap || {}).length,
      p2SubTopicsMap: Object.keys(this.p2SubTopicsMap || {}).length
    });

    // Force a delay to ensure services have time to update
    setTimeout(() => {
      // Save expanded state before updating
      const expandedSubjects = new Set<string>();
      if (this.treeControl.dataNodes) {
        this.treeControl.dataNodes.forEach(node => {
          if (this.treeControl.isExpanded(node)) {
            expandedSubjects.add(node.name);
          }
        });
      }

      // Build the tree data from the input arrays
      if (!this.p1Subjects || this.p1Subjects.length === 0) {
        console.error('No subjects available to build tree - trying to get from treeService');
        // Try to get subjects from TreeService directly
        this.treeService.getTreeData().pipe(take(1)).subscribe(treeData => {
          if (treeData && treeData.subjects) {
            console.log('Got subjects from TreeService:', Object.keys(treeData.subjects));
            this.p1Subjects = Object.keys(treeData.subjects);
            // Now build the tree with the subjects we got
            this.buildTree();
            this.restoreSpecificMainTreeExpansions(expandedSubjects);
          } else {
            console.error('Failed to get subjects from TreeService');
          }
        });
      } else {
        // We have subjects, build the tree
        this.buildTree();
        this.restoreSpecificMainTreeExpansions(expandedSubjects);
      }
    }, 100);
  }

  // New method to build the tree from p1Subjects
  private buildTree(): void {
    console.log(`Building tree with ${this.p1Subjects?.length || 0} subjects`);

    if (!this.p1Subjects || this.p1Subjects.length === 0) {
      console.error('No subjects available to build tree');
      this.treeData = [];
      this.dataSource.data = [];
      return;
    }

    this.treeData = this.p1Subjects.map(subject => {
      // Check if we have sections for this subject in p1SubMap
      const sections = this.p1SubMap[subject] || [];

      // Get subject ID from topicIdMap if available
      const subjectMetadata = this.topicIdMap[subject];
      const subjectTopicId = subjectMetadata ? subjectMetadata.id : undefined;
      const subjectHasChildren = subjectMetadata ? subjectMetadata.hasChildren : sections.length > 0;
      const subjectHindiName = subjectMetadata ? subjectMetadata.hindiName : this.hindiNameMap[subject];

      // Create node for each section
      const sectionNodes: SubjectNode[] = sections.map(section => {
        // Get section ID from topicIdMap if available
        const sectionMetadata = this.topicIdMap[section];
        const sectionTopicId = sectionMetadata ? sectionMetadata.id : undefined;
        // Sections are always leaf nodes in Pane 1 tree - their children (topics)
        // are displayed in Pane 2 when a section is selected
        const sectionHasChildren = false;
        const sectionHindiName = sectionMetadata ? sectionMetadata.hindiName : this.hindiNameMap[section];

        return {
          name: section,
          type: 'section',
          hindiName: sectionHindiName,
          level: 1,
          topicId: sectionTopicId,
          hasChildren: sectionHasChildren
        } as SubjectNode;
      });

      // Create node for the subject with its sections as children
      return {
        name: subject,
        type: 'subject',
        children: sectionNodes,
        hindiName: subjectHindiName,
        level: 0,
        topicId: subjectTopicId,
        hasChildren: subjectHasChildren
      };
    });

    console.log(`Built ${this.treeData.length} top-level nodes for the tree`);

        // Update the data source with the new tree data
    this.dataSource.data = this.treeData;

    // Initialize tree control dataNodes explicitly to ensure toggle functions work
    this.treeControl.dataNodes = this.treeData;

    // Force a change detection cycle
    this.changeDetectorRef.detectChanges();
  }

  // Update the yearwise tree data
  updateYearwiseTreeData(): void {
    // Build the tree data from the input arrays
    this.yearwiseTreeData = this.p1YearwiseExams.map((exam) => {
      const papers = this.p1YearwiseLower[exam] || [];

      // Get Hindi name for this exam from mapping
      const examHindiName = this.hindiNameMap[exam];

      return {
        name: exam,
        hindiName: examHindiName,
        type: 'exam' as 'exam',
        children: papers.map(paper => {
          // Get Hindi name for this paper from mapping
          const paperHindiName = this.hindiNameMap[paper];

          return {
            name: paper,
            hindiName: paperHindiName,
            type: 'paper' as 'paper'
          };
        })
      };
    });

    // Update the data source
    this.yearwiseDataSource.data = this.yearwiseTreeData;

    // Important: Make sure yearwiseTreeControl.dataNodes is initialized
    if (this.yearwiseTreeData.length > 0) {
      // Explicitly set the dataNodes property on the yearwiseTreeControl
      this.yearwiseTreeControl.dataNodes = this.yearwiseTreeData;

      // If in yearwise mode, always expand the tree by default
      if (this.p1Mode === 'yearwise') {
        setTimeout(() => {
          this.expandAllYearwiseNodes();

          // Auto-select first items if nothing is selected
          if (!this.selectedYearwiseExam && this.p1YearwiseExams.length > 0) {
            const firstExam = this.p1YearwiseExams[0];
            this.onYearwiseExamClick(firstExam);
          }
        }, 100);
      }
    }
  }

  // Update the topic tree data
  updateTopicTreeData(): void {
    console.log('NavigationApp: updateTopicTreeData called');
    console.log('Selected section:', this.selectedSubItem);

    // Save the expanded state of all nodes before updating
    const expandedNodeMap = new Map<string, boolean>();
    if (this.topicTreeControl.dataNodes) {
      this.topicTreeControl.dataNodes.forEach(node => {
        expandedNodeMap.set(node.name, this.topicTreeControl.isExpanded(node));
      });
    }

    // Get topics for the selected section
    const topics = this.selectedSubItem ? (this.p2TopicsMap[this.selectedSubItem] || []) : [];
    console.log(`Found ${topics.length} topics for section: ${this.selectedSubItem}`);

    // Build the tree data from the topics
    this.topicTreeData = topics.map((topic) => {
      // Get initial subtopics
      const subtopics = this.p2SubTopicsMap[topic] || [];

      // Check if this topic has metadata from parent component
      const topicMetadata = this.topicIdMap[topic];

      // First check if there are visible subtopics
      const hasVisibleChildren = subtopics.length > 0;

      // Then check metadata flag
      const hasChildrenFromMetadata = topicMetadata ? topicMetadata.hasChildren : false;

      // Only set hasChildren to true if either condition is met
      const shouldHaveChildren = hasVisibleChildren || hasChildrenFromMetadata;

      // Get Hindi name for this topic from metadata or mapping
      const hindiName = topicMetadata?.hindiName || this.hindiNameMap[topic];

      // Create nodes for the subtopics using recursive approach for deeper levels
      const subtopicNodes = subtopics.map(subtopic => {
        // Check for metadata for this subtopic
        const subtopicMetadata = this.topicIdMap[subtopic];
        const subtopicHasChildrenFlag = subtopicMetadata ? subtopicMetadata.hasChildren : false;
        const subtopicHindiName = subtopicMetadata?.hindiName || this.hindiNameMap[subtopic];

        // Create the level 4 node (subtopic)
        const subtopicNode: TopicNode = {
          name: subtopic,
          hindiName: subtopicHindiName,
          type: 'subtopic',
          level: 4,
          hasChildren: subtopicHasChildrenFlag,
          topicId: subtopicMetadata?.id
        };

        // Attempt to get deeper level nodes (level 5+)
        this.loadDeeperLevelNodes(subtopicNode);

        return subtopicNode;
      });

      return {
        name: topic,
        hindiName: hindiName,
        type: 'topic',
        children: subtopicNodes,
        level: 3,
        hasChildren: shouldHaveChildren,
        topicId: topicMetadata?.id
      };
    });

    // Update the data source
    this.topicDataSource.data = this.topicTreeData;

    // Make sure topicTreeControl.dataNodes is initialized
    this.topicTreeControl.dataNodes = this.topicTreeData;

    // Restore the expanded state
    this.restoreTopicTreeExpansion(expandedNodeMap);

    // Force a change detection cycle
    this.changeDetectorRef.detectChanges();
  }

  // Helper method to recursively load and attach deeper level nodes (level 5-7)
  // This is called for each subtopic node (level 4) to attach its deeper children
  private loadDeeperLevelNodes(node: TopicNode): void {
    // If the node doesn't have a topicId, we can't fetch its canAddNode
    if (!node.topicId) {
      console.log(`Node ${node.name} has no topicId, can't fetch deeper children`);
      return;
    }

    console.log(`LOADING DEEPER LEVELS for node: "${node.name}", ID: ${node.topicId}, level: ${node.level}`);

    // Check the TreeService to see if we have data for deeper levels
    this.treeService.getTreeData().pipe(take(1)).subscribe(treeData => {
      // This is a complex traversal of the hierarchical data to find deeper level nodes
      try {
        console.log(`Attempting to find deeper children for node: ${node.name} with ID: ${node.topicId}`);
        // Find the subject that contains this node
        const subjects = treeData.subjects;

        // Flag to track if we found the node
        let foundNode = false;

        for (const subjectName in subjects) {
          const subject = subjects[subjectName];
          // Search in each section
          for (const sectionName in subject.sections) {
            const section = subject.sections[sectionName];
            // Search in each topic
            for (const topicName in section.topics) {
              const topic = section.topics[topicName];
              // Search in each subtopic
              for (const subtopicName in topic.subtopics) {
                const subtopic = topic.subtopics[subtopicName];

                // If this subtopic's ID matches our node's topicId, we've found our match
                if (subtopic.id === node.topicId) {
                  console.log(`Found matching subtopic for ID ${node.topicId}: ${subtopicName}`);
                  foundNode = true;

                  // Check if this subtopic has level5 nodes
                  if (subtopic.level5 && Object.keys(subtopic.level5).length > 0) {
                    console.log(`✅ Subtopic has ${Object.keys(subtopic.level5).length} level5 children`);

                    // Create level 5 nodes and attach them to our node
                    const level5Nodes: TopicNode[] = [];

                    for (const level5Name in subtopic.level5) {
                      const level5Item = subtopic.level5[level5Name];
                      const hasLevel6 = level5Item.level6 && Object.keys(level5Item.level6).length > 0;

                      const level5Node: TopicNode = {
                        name: level5Name,
                        hindiName: level5Item.hindiName,
                        type: 'subtopic',
                        level: 5,
                        topicId: level5Item.id,
                        hasChildren: hasLevel6,
                        children: [] // Initialize empty array to ensure UI recognizes it can have children
                      };

                      // Check if this level5 node has level6 children
                      if (hasLevel6) {
                        // Create level 6 nodes
                        for (const level6Name in level5Item.level6) {
                          const level6Item = level5Item.level6[level6Name];
                          const hasLevel7 = level6Item.level7 && Object.keys(level6Item.level7).length > 0;

                          const level6Node: TopicNode = {
                            name: level6Name,
                            hindiName: level6Item.hindiName,
                            type: 'subtopic',
                            level: 6,
                            topicId: level6Item.id,
                            hasChildren: hasLevel7,
                            children: [] // Initialize empty array to ensure UI recognizes it can have children
                          };

                          // Check if this level6 node has level7 children
                          if (hasLevel7) {
                            // Create level 7 nodes
                            for (const level7Name in level6Item.level7) {
                              const level7Item = level6Item.level7[level7Name];
                              const level7Node: TopicNode = {
                                name: level7Name,
                                hindiName: level7Item.hindiName,
                                type: 'subtopic',
                                level: 7,
                                topicId: level7Item.id,
                                hasChildren: false // Level 7 is our deepest level
                              };

                              // Push the level7 node to the level6 node's children array
                              // (children array is guaranteed to exist because we initialized it above)
                              level6Node.children!.push(level7Node);
                            }
                          }

                          // Push the level6 node to the level5 node's children array
                          // (children array is guaranteed to exist because we initialized it above)
                          level5Node.children!.push(level6Node);
                        }
                      }

                      level5Nodes.push(level5Node);
                    }

                    // Attach all level5 nodes to our original node
                    node.children = level5Nodes;
                    node.hasChildren = true;

                    console.log(`✅ Added ${level5Nodes.length} level 5+ nodes to "${node.name}"`);

                    // Force update of the tree UI
                    setTimeout(() => {
                      // Update the tree UI and expand the node
                      this.zone.run(() => {
                        this.topicDataSource.data = [...this.topicTreeData];
                        this.changeDetectorRef.detectChanges();

                        // Expand the node to show its children
                        this.topicTreeControl.expand(node);

                        // Expand all children too
                        if (node.children) {
                          node.children.forEach(childNode => {
                            this.topicTreeControl.expand(childNode);
                          });
                        }

                        // Force another change detection cycle
                        setTimeout(() => this.changeDetectorRef.detectChanges(), 100);
                      });
                    }, 100);
                  } else {
                    console.log(`❌ No level5 children found for node "${node.name}"`);
                  }

                  // We found our match, no need to continue searching
                  return;
                }

                // Also check level5 nodes if present
                if (subtopic.level5) {
                  for (const level5Name in subtopic.level5) {
                    const level5Item = subtopic.level5[level5Name];

                    // If this level5 item's ID matches our node's topicId
                    if (level5Item.id === node.topicId) {
                      console.log(`Found matching level5 node for ID ${node.topicId}: ${level5Name}`);
                      foundNode = true;

                      // Check if this level5 has level6 nodes
                      if (level5Item.level6 && Object.keys(level5Item.level6).length > 0) {
                        console.log(`✅ Level5 node has ${Object.keys(level5Item.level6).length} level6 children`);

                        // Create level 6 nodes
                        const level6Nodes: TopicNode[] = [];

                        for (const level6Name in level5Item.level6) {
                          const level6Item = level5Item.level6[level6Name];
                          const hasLevel7 = level6Item.level7 && Object.keys(level6Item.level7).length > 0;

                          const level6Node: TopicNode = {
                            name: level6Name,
                            hindiName: level6Item.hindiName,
                            type: 'subtopic',
                            level: 6,
                            topicId: level6Item.id,
                            hasChildren: hasLevel7,
                            children: [] // Initialize empty array
                          };

                          // Check if this level6 node has level7 children
                          if (hasLevel7) {
                            // Create level 7 nodes
                            for (const level7Name in level6Item.level7) {
                              const level7Item = level6Item.level7[level7Name];
                              const level7Node: TopicNode = {
                                name: level7Name,
                                hindiName: level7Item.hindiName,
                                type: 'subtopic',
                                level: 7,
                                topicId: level7Item.id,
                                hasChildren: false // Level 7 is our deepest level
                              };

                              level6Node.children!.push(level7Node);
                            }
                          }

                          level6Nodes.push(level6Node);
                        }

                        // Attach level6 nodes to our node
                        node.children = level6Nodes;
                        node.hasChildren = true;

                        console.log(`✅ Added ${level6Nodes.length} level 6+ nodes to "${node.name}"`);

                        // Force update of the tree UI
                        setTimeout(() => {
                          this.topicDataSource.data = [...this.topicTreeData];
                          this.changeDetectorRef.detectChanges();

                          // Expand the node to show its children
                          this.topicTreeControl.expand(node);
                        }, 100);
                      } else {
                        console.log(`❌ No level6 children found for level5 node "${node.name}"`);
                      }

                      return;
                    }
                  }
                }
              }
            }
          }
        }

        if (!foundNode) {
          console.log(`❌ No matching node found for ID ${node.topicId} in tree data`);
        }
      } catch (error) {
        console.error('Error loading deeper level nodes:', error);
      }
    });
  }

  // Helper method to restore topic tree expansion state
  private restoreTopicTreeExpansion(expandedNodeMap: Map<string, boolean>): void {
    console.log('Restoring topic tree expansion state');
    if (this.topicTreeControl.dataNodes) {
      this.topicTreeControl.dataNodes.forEach(node => {
        if (expandedNodeMap.get(node.name)) {
          this.topicTreeControl.expand(node);
        }

        // Always expand selected nodes
        if ((node.type === 'topic' && node.name === this.selectedTopic) ||
            (node.type === 'subtopic' && node.name === this.selectedSubTopic)) {
          console.log(`Expanding selected node: ${node.name}`);
          this.topicTreeControl.expand(node);
        }
      });
    }
  }

  // Format node name based on selected language and available Hindi names
  formatNodeName(englishName: string, hindiName?: string): string {
    try {
      // Normalize language to lowercase for consistent comparison
      const language = this.selectedLanguage.toLowerCase();

      // Make sure Hindi name isn't the same as English name when in English mode
      // This addresses the issue where Hindi names remain visible in English mode
      const actualHindiName = hindiName || this.hindiNameMap[englishName] || '';

      // Enhanced debugging for Hindi mode
      if (language === 'hindionly') {
        // If we're falling back to English, warn more prominently
        if (!actualHindiName && !hindiName && !this.hindiNameMap[englishName]) {
          console.warn(`WARNING: No Hindi name found for "${englishName}"`);
        }
      }

      // Use lowercase for consistent case comparison
      switch (language) {
        case 'bilingual':
          // Add proper classes and structure for bilingual mode
          return `<div class="bilingual-container"><div class="english-part">${englishName}</div><div class="hindi-part">${actualHindiName || englishName}</div></div>`;
        case 'englishonly':
          // Only show English name, never the Hindi name
          return `<div class="english-only">${englishName}</div>`;
        case 'hindionly':
          // Use Hindi name if available, fallback to English
          return `<div class="hindi-only">${actualHindiName || englishName}</div>`;
        default:
          return `<div class="english-only">${englishName}</div>`;
      }
    } catch (error) {
      console.error('Error formatting node name:', error);
      return englishName; // Fallback to English name in case of errors
    }
  }

  // Update Hindi names map and refresh the tree views accordingly
  updateHindiNames(hindiNames: { [key: string]: string }): void {
    console.log(`Updating ${Object.keys(hindiNames).length} Hindi names in navigation component`);

    // Log sample entries to verify content
    const sampleEntries = Object.entries(hindiNames).slice(0, 5);
    sampleEntries.forEach(([english, hindi]) => {
      // console.log(`  - "${english}" → "${hindi}"`);
    });

    // Show what's already in the map for comparison
    const existingCount = Object.keys(this.hindiNameMap).length;
    // console.log(`Current hindiNameMap has ${existingCount} entries`);

    if (existingCount > 0) {
      const existingSamples = Object.entries(this.hindiNameMap).slice(0, 3);
      existingSamples.forEach(([english, hindi]) => {
        // console.log(`  - "${english}" → "${hindi}"`);
      });
    }

    // Check if we receive known subject names
    const knownSubjects = ['Polity', 'History', 'Economy', 'Science', 'Geography'];
    knownSubjects.forEach(subject => {
      // console.log(`  - "${subject}": ${hindiNames[subject] || 'NOT FOUND'}`);
    });

    // Merge new Hindi names into the existing map
    this.hindiNameMap = { ...this.hindiNameMap, ...hindiNames };

    // Update the tree nodes with the Hindi names
    if (this.treeData && this.treeData.length > 0) {
      this.updateTreeNodesWithHindi(this.treeData);
    }

    if (this.yearwiseTreeData && this.yearwiseTreeData.length > 0) {
      this.updateYearwiseTreeNodesWithHindi(this.yearwiseTreeData);
    }

    if (this.topicTreeData && this.topicTreeData.length > 0) {
      this.updateTopicTreeNodesWithHindi(this.topicTreeData);
    }

    // Force update tree data to reflect new Hindi names
    this.updateTreeData();
    this.updateYearwiseTreeData();
    this.updateTopicTreeData();

    // Refresh the tree UI
    this.refreshTreeUI();

    console.log('Hindi names updated and trees refreshed');
  }

  /**
   * Refresh the topic tree by clearing the cache and reloading data
   */
  refreshTopicTree(): void {
    console.log('NavigationApp - Refreshing topic tree data using TreeService');
    console.log('👉 REFRESH BUTTON CLICKED: Making immediate API call to refresh tree');

    // Show loading indicator if needed
    this.isLoadingTopics = true;

    // Directly call TreeService to immediately refresh data from the complete-structure API
    this.treeService.refreshTree().then(() => {
      console.log('✅ Tree data refresh completed successfully');

      // Update UI with the new data
      this.treeService.getTreeData().pipe(take(1)).subscribe(treeData => {
        console.log('✅ Updating UI with fresh tree data');


        // Extract and update subjects and relationships from the new tree data
        if (treeData && treeData.subjects) {
          this.p1Subjects = Object.keys(treeData.subjects);

          // Extract section mappings and subject IDs
          Object.keys(treeData.subjects).forEach(subject => {
            // Extract subject ID information
            const subjectData = treeData.subjects[subject];
            if (subjectData.id) {
              this.topicIdMap[subject] = {
                id: subjectData.id,
                level: 1, // Subjects are level 1
                hasChildren: subjectData.sections && Object.keys(subjectData.sections).length > 0,
                hindiName: subjectData.hindiName
              };
            }

            if (subjectData.sections) {
              this.p1SubMap[subject] = Object.keys(subjectData.sections);

              // Extract section IDs
              Object.keys(subjectData.sections).forEach(section => {
                const sectionData = subjectData.sections[section];
                
                // Store section ID information
                if (sectionData.id) {
                  this.topicIdMap[section] = {
                    id: sectionData.id,
                    level: 2, // Sections are level 2
                    hasChildren: sectionData.topics && Object.keys(sectionData.topics).length > 0,
                    hindiName: sectionData.hindiName
                  };
                }

                if (sectionData.topics) {
                  this.p2TopicsMap[section] = Object.keys(sectionData.topics);

                  // Extract subtopics for each topic
                  Object.keys(sectionData.topics).forEach(topic => {
                    if (sectionData.topics[topic].subtopics) {
                      this.p2SubTopicsMap[topic] = Object.keys(
                        sectionData.topics[topic].subtopics
                      );

                      // Also extract ID and level information for this topic
                      const topicData = sectionData.topics[topic];
                      this.topicIdMap[topic] = {
                        id: topicData.id,
                        level: 3, // Topics are level 3
                        hasChildren: Object.keys(topicData.subtopics).length > 0,
                        hindiName: topicData.hindiName
                      };

                      // Extract metadata for subtopics
                      Object.keys(topicData.subtopics).forEach(subtopic => {
                        const subtopicData = topicData.subtopics[subtopic];
                        const hasLevel5 = !!(subtopicData.level5 && Object.keys(subtopicData.level5).length > 0);
                        this.topicIdMap[subtopic] = {
                          id: subtopicData.id,
                          level: 4, // Subtopics are level 4
                          hasChildren: hasLevel5,
                          hindiName: subtopicData.hindiName
                        };
                      });
                    }
                  });
                }
              });
            }
          });
        }

        // Stop loading indicator
        this.isLoadingTopics = false;

        // Update both pane1 and pane2 tree UI
        this.updateTreeData();
        this.updateTopicTreeData();

        // Expand the selected nodes after refresh to maintain navigation context
        setTimeout(() => {
          // Expand nodes in pane1 if a subject is selected
          if (this.selectedSubject) {
            this.expandTreeForNode(this.selectedSubject);
          }

          // Expand nodes in pane2 if a topic is selected
          if (this.selectedTopic && this.topicTreeControl.dataNodes) {
            const topicNode = this.topicTreeControl.dataNodes.find(node => node.name === this.selectedTopic);
            if (topicNode) {
              this.topicTreeControl.expand(topicNode);

              // If there are any subtopics selected, expand those too
              if (this.selectedSubTopic) {
                // Find the subtopic node
                const subtopicNode = this.topicTreeControl.dataNodes
                  .flatMap(node => (node.children || []))
                  .find(node => node.name === this.selectedSubTopic);

                if (subtopicNode) {
                  this.topicTreeControl.expand(subtopicNode);
                }
              }
            }
          }

        }, 300);

        // Tree has already been refreshed via TreeService in this method.
        // Do not emit another refresh request to parent, or it will trigger
        // a duplicate API refresh cycle.
      });

    }).catch(error => {
      console.error('❌ Error refreshing tree data:', error);
      // Stop loading indicator on error
      this.isLoadingTopics = false;
    });

    // Reset any tree-related state as needed
    this.editingNode = null;
  }

  /**
   * Update main tree nodes with Hindi names
   */
  private updateTreeNodesWithHindi(nodes: SubjectNode[]): void {
    nodes.forEach(node => {
      // Update Hindi name if available in the map
      if (this.hindiNameMap[node.name]) {
        node.hindiName = this.hindiNameMap[node.name];
      }

      // Recursively update children
      if (node.children && node.children.length > 0) {
        this.updateTreeNodesWithHindi(node.children);
      }
    });
  }

  /**
   * Update yearwise tree nodes with Hindi names
   */
  private updateYearwiseTreeNodesWithHindi(nodes: YearwiseNode[]): void {
    nodes.forEach(node => {
      // Update Hindi name if available in the map
      if (this.hindiNameMap[node.name]) {
        node.hindiName = this.hindiNameMap[node.name];
      }

      // Recursively update children
      if (node.children && node.children.length > 0) {
        this.updateYearwiseTreeNodesWithHindi(node.children);
      }
    });
  }

  /**
   * Update topic tree nodes with Hindi names
   */
  private updateTopicTreeNodesWithHindi(nodes: TopicNode[]): void {
    nodes.forEach(node => {
      // Update Hindi name if available in the map
      if (this.hindiNameMap[node.name]) {
        node.hindiName = this.hindiNameMap[node.name];
      }

      // Recursively update children
      if (node.children && node.children.length > 0) {
        this.updateTopicTreeNodesWithHindi(node.children);
      }
    });
  }

  // Returns the icon for current mode
  getModeIcon(): string {
    return this.p1Mode === 'topicwise' ? 'category' : 'event_note';
  }

  getModeLabel(): string {
    return this.p1Mode === 'topicwise' ? 'Topics' : 'Years';
  }

  // Toggle collapse state for pane2 only
  togglePane2Collapse(): void {
    this.isPaneTwo_Collapsed = !this.isPaneTwo_Collapsed;
    // You can add an EventEmitter if needed for parent components to react
  }

  // Toggle collapse state for pane1 only
  togglePane1Collapse(): void {
    this.isPaneOne_Collapsed = !this.isPaneOne_Collapsed;
    // You can add an EventEmitter if needed for parent components to react
  }

  // Handler for View button click - now opens the menu instead of emitting an event
  onViewButtonClick(): void {
    if (this.fontSizeMenuTrigger) {
      this.fontSizeMenuTrigger.openMenu();
    } else {
      // If menu not ready, still emit the original event for backward compatibility
      this.viewButtonClick.emit();
    }
  }

  // Handler for Settings button click
  onSettingsButtonClick(): void {
    this.settingsButtonClick.emit();
  }

  // Enhanced method to handle font size selection
  setFontSize(size: FontSizeOption): void {
    this.fontSizeService.setFontSize(size);
    this.fontSizeChange.emit(size);

    // Force refresh of component view to ensure all elements update
    setTimeout(() => {
      this.refreshTreeUI();
      this.changeDetectorRef.detectChanges();
    }, 100);
  }

  // Helper method to find the parent subject of a section
  findParentFromTreeData(sectionName: string): string | null {
    // First try to find in the tree data structure which is more efficient
    if (this.treeData && this.treeData.length > 0) {
      for (const subject of this.treeData) {
        if (subject.children && subject.children.length > 0) {
          for (const section of subject.children) {
            if (section.name === sectionName) {
              return subject.name;
            }
          }
        }
      }
    }

    // Fallback to the map-based search if not found in the tree
    for (const subject of this.p1Subjects) {
      const sections = this.p1SubMap[subject] || [];
      if (sections.includes(sectionName)) {
        return subject;
      }
    }
    return null;
  }

  // Update updateNavigationPath method
  private updateNavigationPath(): void {
    const pathItems: NavigationPathItem[] = [];

    if (this.p1Mode === 'topicwise') {
      // Add subject if available
      if (this.selectedSubject) {
        pathItems.push({
          name: this.selectedSubject,
          hindiName: this.hindiNameMap[this.selectedSubject],
          type: 'subject'
        });

        // Add section if available
        if (this.selectedSubItem) {
          pathItems.push({
            name: this.selectedSubItem,
            hindiName: this.hindiNameMap[this.selectedSubItem],
            type: 'section'
          });

          // Add topic if available
          if (this.selectedTopic) {
            pathItems.push({
              name: this.selectedTopic,
              hindiName: this.hindiNameMap[this.selectedTopic],
              type: 'topic'
            });

            // Add parent subtopics if any
            const parentSubtopics = this.getOrderedParentSubtopics();
            for (const parentSubtopic of parentSubtopics) {
              if (parentSubtopic.type === 'subtopic' && parentSubtopic.name !== this.selectedSubTopic) {
                pathItems.push({
                  name: parentSubtopic.name,
                  hindiName: parentSubtopic.hindiName || this.hindiNameMap[parentSubtopic.name],
                  type: 'subtopic'
                });
              }
            }

            // Add selected subtopic if available
            if (this.selectedSubTopic) {
              pathItems.push({
                name: this.selectedSubTopic,
                hindiName: this.hindiNameMap[this.selectedSubTopic],
                type: 'subtopic'
              });
            }
          }
        }
      }
    } else if (this.p1Mode === 'yearwise') {
      // Add yearwise exam if available
      if (this.selectedYearwiseExam) {
        pathItems.push({
          name: this.selectedYearwiseExam,
          hindiName: this.hindiNameMap[this.selectedYearwiseExam],
          type: 'exam'
        });

        // Add paper type if available
        if (this.selectedYearwiseLower) {
          pathItems.push({
            name: this.selectedYearwiseLower,
            hindiName: this.hindiNameMap[this.selectedYearwiseLower],
            type: 'paper'
          });

          // Add year if available
          if (this.selectedYear) {
            pathItems.push({
              name: this.selectedYear,
              hindiName: this.hindiNameMap[this.selectedYear],
              type: 'year'
            });
          }
        }
      }
    }

    // Update the navigation path service with the new path items
    this.navigationPathService.updateNavigationPath(pathItems);
  }

  // Add ngOnChanges to update navigation path when inputs change
  ngOnChanges(changes: SimpleChanges): void {
    // Check if any navigation-related inputs have changed
    const navigationInputs = [
      'selectedSubject', 'selectedSubItem', 'selectedTopic', 'selectedSubTopic',
      'selectedYearwiseExam', 'selectedYearwiseLower', 'selectedYear'
    ];

    const hasNavigationChanges = navigationInputs.some(input => changes[input]);

    if (hasNavigationChanges) {
      // Update navigation path when inputs change
      this.updateNavigationPath();
    }
  }

  // Add method to fetch yearwise data
  fetchYearwiseData(): void {
    this.dataService.getYearwiseData()
      .pipe(
        finalize(() => {
          // Update the yearwise tree with the fetched data
          this.updateYearwiseTreeData();

          // Ensure tree is expanded and first items are selected
          setTimeout(() => {
            this.expandAllYearwiseNodes();

            // Auto-select first items if nothing is selected
            if (!this.selectedYearwiseExam && this.p1YearwiseExams.length > 0) {
              const firstExam = this.p1YearwiseExams[0];
              this.onYearwiseExamClick(firstExam);
            }
          }, 100);
        })
      )
      .subscribe(
        (response) => {
          if (response && response.exams) {
            // Map the response to our data structure
            this.p1YearwiseExams = response.exams.map(exam => exam.exam_name);

            // Create the yearwise lower map
            this.p1YearwiseLower = {};
            this.p2Years = {}; // Reset years map

            // We need to map Paper objects to paper_name strings
            response.exams.forEach(exam => {
              this.p1YearwiseLower[exam.exam_name] = exam.papers.map(paper => paper.paper_name);

              // Store years for each paper
              exam.papers.forEach(paper => {
                // Convert Year objects to strings and sort in descending order
                this.p2Years[paper.paper_name] = paper.years
                  .map(year => year.year.toString())
                  .sort((a, b) => parseInt(b) - parseInt(a));
              });
            });

            // Generate years for all papers (2025 to 1990)
            this.generateYearsForPapers();

            // Auto-select first exam if in yearwise mode and no exam is selected
            if (this.p1Mode === 'yearwise' && this.p1YearwiseExams.length > 0) {
              const firstExam = this.p1YearwiseExams[0];

              // Don't auto-select if an exam is already selected (unless forced)
              if (!this.selectedYearwiseExam) {
                console.log(`NavigationApp - Auto-selecting first exam: ${firstExam}`);
                this.onYearwiseExamClick(firstExam);
              }
              // If exam is selected but paper is not, select first paper
              else if (this.selectedYearwiseExam && !this.selectedYearwiseLower) {
                // If the selected exam has papers, select the first one
                if (this.p1YearwiseLower[this.selectedYearwiseExam] &&
                    this.p1YearwiseLower[this.selectedYearwiseExam].length > 0) {
                  const firstPaper = this.p1YearwiseLower[this.selectedYearwiseExam][0];
                  console.log(`NavigationApp - Auto-selecting first paper: ${firstPaper} for exam: ${this.selectedYearwiseExam}`);
                  this.selectedYearwiseLower = firstPaper;
                  this.yearwiseLowerClick.emit(firstPaper);

                  // And if year is not selected, select first year
                  if (!this.selectedYear && this.p2Years[firstPaper] && this.p2Years[firstPaper].length > 0) {
                    const firstYear = this.p2Years[firstPaper][0];
                    console.log(`NavigationApp - Auto-selecting first year: ${firstYear}`);
                    this.selectedYear = firstYear;
                    this.yearClick.emit(firstYear);
                  }
                }
              }
              // If exam and paper are selected but year is not, select first year
              else if (this.selectedYearwiseExam && this.selectedYearwiseLower && !this.selectedYear) {
                if (this.p2Years[this.selectedYearwiseLower] && this.p2Years[this.selectedYearwiseLower].length > 0) {
                  const firstYear = this.p2Years[this.selectedYearwiseLower][0];
                  console.log(`NavigationApp - Auto-selecting first year: ${firstYear}`);
                  this.selectedYear = firstYear;
                  this.yearClick.emit(firstYear);
                }
              }
            }
          }
        },
        (error) => {
          console.error('Error fetching yearwise data:', error);
        }
      );
  }

  // Method to process years from API data
  generateYearsForPapers(): void {
    console.log('Processing years from API data');

    // We need to pre-fill p2Years with empty arrays to prevent errors
    Object.keys(this.p1YearwiseLower).forEach(exam => {
      const papers = this.p1YearwiseLower[exam] || [];
      papers.forEach(paperName => {
        if (!this.p2Years[paperName]) {
          this.p2Years[paperName] = [];
        }
      });
    });

    // If we have years for at least one paper, emit event
    const firstExam = this.p1YearwiseExams[0];
    if (firstExam && this.p1YearwiseLower[firstExam] && this.p1YearwiseLower[firstExam].length > 0) {
      const firstPaper = this.p1YearwiseLower[firstExam][0];
      if (this.p2Years[firstPaper] && this.p2Years[firstPaper].length > 0) {
        this.yearsListGenerated.emit(this.p2Years[firstPaper]);
      }
    }

    // Auto-select first year if we're in yearwise mode with an exam and paper selected
    if (this.p1Mode === 'yearwise' &&
        this.selectedYearwiseExam &&
        this.selectedYearwiseLower &&
        !this.selectedYear) {

      if (this.p2Years[this.selectedYearwiseLower] &&
          this.p2Years[this.selectedYearwiseLower].length > 0) {

        const firstYear = this.p2Years[this.selectedYearwiseLower][0];
        console.log(`NavigationApp - Auto-selecting first year (${firstYear}) after processing years`);
        this.selectedYear = firstYear;
        this.yearClick.emit(firstYear);
      }
    }
    // If no exam or paper is selected yet, but we're in yearwise mode, auto-select first exam
    else if (this.p1Mode === 'yearwise' &&
             !this.selectedYearwiseExam &&
             this.p1YearwiseExams.length > 0) {

      console.log(`NavigationApp - Auto-selecting first exam after processing years`);
      this.onYearwiseExamClick(this.p1YearwiseExams[0]);
    }
  }

  /**
   * Toggle edit mode for tree
   */
  toggleEditMode(event: any): void {
    this.isEditMode = event.checked;
    
    // Emit edit mode change event to parent component
    this.editModeChange.emit(this.isEditMode);
    
    if (!this.isEditMode) {
      // Clear any active editing state
      this.editingNode = null;
    }
  }

  /**
   * Show edit options for a node
   */
  showEditOptions(event: Event, node: any): void {
    event.stopPropagation();
    this.editingNode = node;
  }

  /**
   * Add a new node
   */
  addNode(parentNode: any): void {
    console.log('Adding node under:', parentNode);
    console.log('ParentNode level:', parentNode.level, 'type:', parentNode.type);
    console.log('ParentNode topicId:', parentNode.topicId);

    // Determine the correct parent_topic_id and item_level
    let parentTopicId = '1';
    let itemLevel = 0;

    // First check if parent node has a level property we can use
    if (parentNode.level !== undefined) {
      // Child level should match what's displayed in the UI (not parent + 1)
      // The database already starts level from 1, so we should use level as shown in UI
      itemLevel = parentNode.level + 2;
      console.log(`Setting item_level to parent level + 2: ${itemLevel}`);
    }

    // Determine parent_topic_id
    if (parentNode.topicId) {
      parentTopicId = parentNode.topicId;
      console.log(`Using parent node's topicId: ${parentTopicId}`);
    } else if (parentNode.children && parentNode.children.length > 0) {
      // Try to extract parent id pattern from children's topicIds if available
      for (const child of parentNode.children) {
        if (child.topicId) {
          // Extract parent part of topicId (e.g., "1.1" from "1.1.10")
          const parts = child.topicId.split('.');
          if (parts.length >= 2) {
            parentTopicId = parts.slice(0, 2).join('.');
            console.log(`Extracted parent_topic_id ${parentTopicId} from child's topicId ${child.topicId}`);
            break;
          }
        }
      }
    }

    console.log('Determined values - parent_topic_id:', parentTopicId, 'item_level for child:', itemLevel, '(DB level will be this value)');

    // Open dialog to get new topic details
    const dialogRef = this.dialog.open(TopicDialogComponent, {
      width: '500px',
      data: {
        parentTopicId: parentTopicId,
        parentName: parentNode.name,
        parentLevel: itemLevel // Pass parent's actual level for UI display
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Create API payload
        const payload = {
          name_eng: result.nameEng,
          name_hindi: result.nameHindi || undefined,
          parent_topic_id: parentTopicId,
          item_level: itemLevel
        };

        // Call API
        this.treeEditService.createTopic(payload).subscribe({
          next: (response) => {
            console.log('Topic created successfully:', response);
            
            // Refresh the tree to get the updated structure from API
            this.refreshTopicTree();
            
            // Show success message
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: {
                title: 'Success',
                message: `"${result.nameEng}" has been created successfully.`,
                confirmText: 'OK',
                cancelText: ''
              }
            });
          },
          error: (error) => {
            console.error('Error creating topic:', error);
            // Show error message
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: {
                title: 'Error',
                message: `Failed to create topic: ${error.error?.message || 'Unknown error'}`,
                confirmText: 'OK',
                cancelText: ''
              }
            });
          }
        });
      }
    });


  }

  /**
   * Edit/rename a node
   */
  editNode(node: any): void {
    try {
      console.log('EditNode method entered', new Date().toISOString());

      // Check if dialog service is properly injected
      if (!this.dialog) {
        console.error('Error: Dialog service is not available');
        return;
      }

      console.log('EditNode method called with node:', node);
      console.log('Node properties:', Object.keys(node));

      // Get topic ID - either from node directly or from topicIdMap
      let topicId = node.topicId;

      // If topicId is not available directly on the node, try to get it from the map
      if (!topicId && this.topicIdMap && this.topicIdMap[node.name]) {
        topicId = this.topicIdMap[node.name].id;
        console.log('Retrieved topicId from topicIdMap:', topicId);
      }

      console.log('Node topic ID (final):', topicId);

      if (!topicId) {
        console.error('Cannot rename: No topic ID found');
        // Show error message to user
        this.dialog.open(ConfirmDialogComponent, {
          width: '400px',
          data: {
            title: 'Error',
            message: `Cannot rename "${node.name}": Topic ID not found. The tree may need to be refreshed.`,
            confirmText: 'OK',
            cancelText: ''
          }
        });
        return;
      }

      console.log('Opening rename dialog');

      // Show rename dialog
      setTimeout(() => {
        try {
          const dialogRef = this.dialog.open(TopicDialogComponent, {
            width: '500px',
            data: {
              parentTopicId: topicId,
              parentName: node.name,
              parentLevel: node.level || 1, // Show actual level in UI
              isRename: true,
              existingNameEng: node.name,
              existingNameHindi: node.hindiName,
              topicId: topicId
            }
          });

          console.log('Rename dialog opened successfully');

          dialogRef.afterClosed().subscribe(result => {
            console.log('Dialog closed with result:', result);
            if (result) {
              console.log('About to call rename API with topicId:', topicId);

              // Prepare API payload
              const payload = {
                name_eng: result.nameEng || undefined,
                name_hindi: result.nameHindi || undefined
              };

              // Ensure at least one name is provided
              if (!payload.name_eng && !payload.name_hindi) {
                console.error('Cannot save edit: No names provided');
                return;
              }

              // Call rename API
              this.treeEditService.renameTopic(topicId, payload).subscribe({
                next: (response) => {
                  console.log('Topic renamed successfully:', response);

                  // Refresh the tree to get the updated data from API
                  this.refreshTopicTree();
                  
                  // Show success message
                  this.dialog.open(ConfirmDialogComponent, {
                    width: '400px',
                    data: {
                      title: 'Success',
                      message: `"${node.name}" has been renamed successfully.`,
                      confirmText: 'OK',
                      cancelText: ''
                    }
                  });
                },
                error: (error) => {
                  console.error('Error renaming topic:', error);

                  let errorMessage = 'Unknown error occurred';
                  if (error.status === 400) {
                    errorMessage = 'Cannot rename topic: Bad request.';
                  } else if (error.status === 404) {
                    errorMessage = 'Topic not found.';
                  } else if (error.error?.message) {
                    errorMessage = error.error.message;
                  }

                  // Show error message
                  this.dialog.open(ConfirmDialogComponent, {
                    width: '400px',
                    data: {
                      title: 'Error',
                      message: `Failed to rename topic: ${errorMessage}`,
                      confirmText: 'OK',
                      cancelText: ''
                    }
                  });
                }
              });
            }
          });
        } catch (dialogError) {
          console.error('Error opening dialog:', dialogError);
        }
      }, 100); // Small delay before opening dialog
    } catch (error) {
      console.error('Error in editNode method:', error);
    }
  }

    /**   * Delete a node   */

  /**
   * Delete a node
   */
  deleteNode(node: any): void {
    try {
      console.log('DeleteNode method entered', new Date().toISOString());

      // Check if dialog service is properly injected
      if (!this.dialog) {
        console.error('Error: Dialog service is not available');
        return;
      }

      console.log('DeleteNode method called with node:', node);
      console.log('Node properties:', Object.keys(node));

      // Get topic ID - either from node directly or from topicIdMap
      let topicId = node.topicId;

      // If topicId is not available directly on the node, try to get it from the map
      if (!topicId && this.topicIdMap && this.topicIdMap[node.name]) {
        topicId = this.topicIdMap[node.name].id;
        console.log('Retrieved topicId from topicIdMap:', topicId);
      }

      console.log('Node topic ID (final):', topicId);

      if (!topicId) {
        console.error('Cannot delete: No topic ID found');
        // Show error message to user
        this.dialog.open(ConfirmDialogComponent, {
          width: '400px',
          data: {
            title: 'Error',
            message: `Cannot delete "${node.name}": Topic ID not found. The tree may need to be refreshed.`,
            confirmText: 'OK',
            cancelText: ''
          }
        });
        return;
      }

      console.log('Opening confirmation dialog for deletion');

      // Show confirmation dialog
      setTimeout(() => {
        try {
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '400px',
            data: {
              title: 'Confirm Delete',
              message: `Are you sure you want to delete "${node.name}"? This action cannot be undone.`,
              confirmText: 'Delete',
              cancelText: 'Cancel'
            }
          });

          console.log('Dialog opened successfully');

          dialogRef.afterClosed().subscribe(result => {
            console.log('Dialog closed with result:', result);
            if (result) {
              console.log('About to call delete API with topicId:', topicId);
              // Call delete API
              this.treeEditService.deleteTopic(topicId).subscribe({
                next: (response) => {
                  console.log('Topic deleted successfully:', response);

                  // Store node name for success message
                  const nodeName = node.name;
                  
                  // Refresh the tree to get the updated structure from API
                  this.refreshTopicTree();
                  
                  // Show success message
                  this.dialog.open(ConfirmDialogComponent, {
                    width: '400px',
                    data: {
                      title: 'Success',
                      message: `"${nodeName}" has been deleted successfully.`,
                      confirmText: 'OK',
                      cancelText: ''
                    }
                  });
                },
                error: (error) => {
                  console.error('Error deleting topic:', error);

                  let errorMessage = 'Unknown error occurred';
                  if (error.status === 400) {
                    errorMessage = 'Cannot delete: This topic has children. Delete all children first.';
                  } else if (error.status === 404) {
                    errorMessage = 'Topic not found.';
                  } else if (error.error?.message) {
                    errorMessage = error.error.message;
                  }

                  // Show error message
                  this.dialog.open(ConfirmDialogComponent, {
                    width: '400px',
                    data: {
                      title: 'Error',
                      message: `Failed to delete topic: ${errorMessage}`,
                      confirmText: 'OK',
                      cancelText: ''
                    }
                  });
                }
              });
            }
          });
        } catch (dialogError) {
          console.error('Error opening dialog:', dialogError);
        }
      }, 100); // Small delay before opening dialog
    } catch (error) {
      console.error('Error in deleteNode method:', error);
    }
  }

  /**
   * Remove a node from the tree
   */
  private removeNodeFromTree(nodeToRemove: any): void {
    const nodeType = nodeToRemove.type;

    // Helper function to recursively search and remove node
    const findAndRemove = (nodes: any[]): boolean => {
      if (!nodes) return false;

      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i] === nodeToRemove) {
          // Found the node, remove it
          nodes.splice(i, 1);
          return true;
        }

        // Check children
        const children = nodes[i].children || [];
        if (children.length > 0) {
          if (findAndRemove(children)) {
            return true;
          }
        }
      }

      return false;
    };

    // Choose the appropriate tree based on node type
    if (nodeType === 'subject' || nodeType === 'section') {
      if (this.treeData && this.treeData.length > 0) {
        findAndRemove(this.treeData);
      }
    } else if (nodeType === 'topic' || nodeType === 'subtopic') {
      if (this.topicTreeData && this.topicTreeData.length > 0) {
        findAndRemove(this.topicTreeData);
      }
    }
  }

  /**
   * Check if node can be added (up to level 6)
   */
  canAddNode(node: any): boolean {
    return (node.level || 0) <= 6;
  }

  /**
   * Check if node can be edited/deleted (up to level 7)
   */
  canEditDeleteNode(node: any): boolean {
    return (node.level || 0) <= 7;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Get the ID for a topic if available
  getTopicId(topicName: string): string | undefined {
    // Check parent component for topic ID mapping
    if (this.topicIdMap && this.topicIdMap[topicName]) {
      return this.topicIdMap[topicName].id;
    }
    return undefined;
  }

  // Check if a topic has further nested children (beyond standard topic-subtopic)
  hasNestedChildren(topicName: string): boolean {
    // Check parent component for hasChildren flag
    if (this.topicIdMap && this.topicIdMap[topicName]) {
      return this.topicIdMap[topicName].hasChildren;
    }
    return false;
  }

  // Get nested children for a topic (if any)
  getNestedChildren(topicName: string): TopicNode[] {
    // This would be populated from deeper level API calls
    // Currently returning empty, but will be filled in as user navigates
    return [];
  }

  // Toggle collapse state for the navigation
  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.collapseStateChange.emit(this.isCollapsed);
  }

  // Handler for subject toggle button click
  onSubjectToggleClick(event: Event, node: SubjectNode): void {
    // Stop propagation to prevent double handling with node selection
    event.stopPropagation();

    // Toggle the tree node expansion
    this.treeControl.toggle(node);

    // Only for subject type nodes, emit the subject click event
    if (node.type === 'subject') {
      this.selectedSubject = node.name;
     // this.selectedSubItem = null;

      // Emit subject click to load the sections if needed
      this.subjectClick.emit(node.name);


      // If expanding and no children are loaded yet, force the API call
      if (this.treeControl.isExpanded(node) && (!node.children || node.children.length === 0)) {
        this.subjectClick.emit(node.name);
      }
    } else if (node.type === 'section') {
      // Find the parent subject of this section
      const parentSubject = this.findParentFromTreeData(node.name);
      if (parentSubject) {
        this.selectedSubject = parentSubject;
      } else {
        // If parent subject not found, find it using the alternative method
        const altParentSubject = this.findParentSubject(node.name);
        if (altParentSubject) {
          this.selectedSubject = altParentSubject;
        }
      }

      this.selectedSubItem = node.name;
      this.subItemClick.emit(node.name);
    }
  }

  // Update the method to select nodes and apply language handling
  onNodeSelect(node: SubjectNode): void {
    if (this.isSelecting) return; // Prevent multiple selections at once
    this.isSelecting = true;

    // Debug for tracing selection
    console.log('Node selected:', node.name, 'Type:', node.type);

    // Check if this is a subject node (parent) or section node (child/leaf)
    const isSubject = node.type === 'subject' || node.children;
    const isSection = node.type === 'section' || !node.children;

    console.log('Is subject?', isSubject, 'Is section?', isSection);

    if (isSubject) {
      // Use the Hindi name from node or map if available
      const hindiName = node.hindiName || this.hindiNameMap[node.name] || node.name;
      const displayName = this.formatNodeName(node.name, hindiName);

      // Clear previous section and topic selections
      this.selectedSubItem = null;
      this.selectedTopic = null;
      this.selectedSubTopic = null;

      // Set subject selection and emit event
      this.selectedSubject = node.name;
      this.subjectClick.emit(node.name);

      console.log('Selected subject:', this.selectedSubject);
      console.log('Navigation path should show:', this.selectedSubject);

      // Now restore to allow other selections
      setTimeout(() => this.isSelecting = false, 100);
    } else if (isSection) {
      // Section is selected
      // Use the Hindi name from node or map if available
      const hindiName = node.hindiName || this.hindiNameMap[node.name] || node.name;
      const displayName = this.formatNodeName(node.name, hindiName);

      // Find parent subject of this section by looking through the tree data
      const parentSubject = this.findParentFromTreeData(node.name);
      console.log('Found parent subject for section:', parentSubject);

      if (parentSubject) {
        this.selectedSubject = parentSubject;
      } else {
        // If parent subject not found, find it using the alternative method
        const altParentSubject = this.findParentSubject(node.name);
        if (altParentSubject) {
          this.selectedSubject = altParentSubject;
        }
      }

      // Set section selection and emit event
      this.selectedSubItem = node.name;

      // Only clear topic/subtopic selections if selecting a different section
      if (this.selectedSubItem !== node.name) {
        this.selectedTopic = null;
        this.selectedSubTopic = null;
      }

      this.subItemClick.emit(node.name);

      console.log('Selected subject after section selection:', this.selectedSubject);
      console.log('Selected section:', this.selectedSubItem);
      console.log('Navigation path should show:', this.selectedSubject, ' > ', this.selectedSubItem);

      // Now restore to allow other selections
      setTimeout(() => this.isSelecting = false, 100);
    } else {
      // Unknown node type - just reset selection flag
      console.warn('Unknown node type:', node);
      this.isSelecting = false;
    }

    this.updateNavigationPath();
  }

  // Helper method to find the parent subject of a section
  findParentSubject(sectionName: string): string | null {
    for (const subject of this.p1Subjects) {
      const sections = this.p1SubMap[subject] || [];
      if (sections.includes(sectionName)) {
        return subject;
      }
    }
    return null;
  }

  // Handle selection of a subject in collapsed view
  onSubjectSelect(subject: string): void {
    // Set the new subject
    this.selectedSubject = subject;
    this.subjectClick.emit(subject);

    this.updateNavigationPath();
  }

  // Handle selection of a node in the yearwise tree
  onYearwiseNodeSelect(node: YearwiseNode): void {
    if (this.isSelecting) return; // Prevent multiple selections at once
    this.isSelecting = true;

    if (node.type === 'exam') {
      // Use the Hindi name from node or map if available
      const hindiName = node.hindiName || this.hindiNameMap[node.name] || node.name;
      const displayName = this.formatNodeName(node.name, hindiName);
      this.selectedYearwiseExam = node.name;
      this.selectedYearwiseLower = null;
      this.selectedYear = null;
      this.yearwiseExamClick.emit(node.name);
    } else {
      // Use the Hindi name from node or map if available
      const hindiName = node.hindiName || this.hindiNameMap[node.name] || node.name;
      const displayName = this.formatNodeName(node.name, hindiName);
      this.selectedYearwiseLower = node.name;
      this.selectedYear = null;
      this.yearwiseLowerClick.emit(node.name);
    }

    // Now restore to allow other selections
    setTimeout(() => this.isSelecting = false, 100);

    this.updateNavigationPath();
  }

  // Handle both toggle and API call for yearwise nodes
  onYearwiseToggleClick(event: Event, node: YearwiseNode): void {
    // Stop propagation to prevent double handling
    event.stopPropagation();

    // Toggle the node in the tree
    this.yearwiseTreeControl.toggle(node);

    // Also perform the node selection to trigger API calls
    if (node.type === 'exam') {
      this.selectedYearwiseExam = node.name;

      // Always emit the exam click event to ensure papers are loaded
      this.yearwiseExamClick.emit(node.name);

      // If expanding and no children are loaded yet, force the API call
      if (this.yearwiseTreeControl.isExpanded(node) && (!node.children || node.children.length === 0)) {
        this.yearwiseExamClick.emit(node.name);
      }
    } else {
      this.selectedYearwiseLower = node.name;
      this.yearwiseLowerClick.emit(node.name);
    }
  }

  // Handle exam click in collapsed yearwise view
  onYearwiseExamClick(exam: string) {
    console.log(`NavigationApp - Exam clicked: ${exam}`);
    this.selectedYearwiseExam = exam;

    // Auto-select the first paper for this exam if available
    if (this.p1YearwiseLower[exam] && this.p1YearwiseLower[exam].length > 0) {
      const firstPaper = this.p1YearwiseLower[exam][0];
      this.selectedYearwiseLower = firstPaper;

      // Auto-select the first year if available
      if (this.p2Years[firstPaper] && this.p2Years[firstPaper].length > 0) {
        this.selectedYear = this.p2Years[firstPaper][0];
        // Emit year click to ensure it's selected
        this.yearClick.emit(this.selectedYear);
      }

      // Emit lower click to ensure it's selected
      this.yearwiseLowerClick.emit(firstPaper);
    }

    // Emit the exam click last (parent components handle the cascade)
    this.yearwiseExamClick.emit(exam);

    this.updateNavigationPath();
  }

  // Handle year click
  onYearClick(year: string) {
    console.log(`NavigationApp - Year clicked: ${year}`);
    this.selectedYear = year;
    this.yearClick.emit(year);

    this.updateNavigationPath();
  }

  // Also update topic node selection
  onTopicNodeSelect(node: TopicNode): void {
    if (this.isSelecting) return; // Prevent multiple selections at once
    this.isSelecting = true;

    console.log(`Topic node selected: ${node.name}, type: ${node.type}, level: ${node.level}, hasChildren: ${node.hasChildren}`);

    if (node.type === 'topic') {
      // Use the Hindi name from node or map if available
      const hindiName = node.hindiName || this.hindiNameMap[node.name] || node.name;
      const displayName = this.formatNodeName(node.name, hindiName);

      // Set this topic as selected
      this.selectedTopic = node.name;
      this.selectedSubTopic = null;

      // Emit the topic click event
      this.topicClick.emit(node.name);


      // Log for debugging
      console.log(`Selected topic node: ${node.name}, emitting topicClick`);
    } else {
      // Use the Hindi name from node or map if available
      const hindiName = node.hindiName || this.hindiNameMap[node.name] || node.name;
      const displayName = this.formatNodeName(node.name, hindiName);

      // Set this subtopic as selected
      this.selectedSubTopic = node.name;

      // Find parent topic/subtopic path
      const parentPath = this.findParentNodesPath(node.name);

      // If this is a deep subtopic, find the top-level topic to set correctly
      if (parentPath.length > 0) {
        // Find the top-level topic node
        const topicNode = parentPath.find(parent => parent.type === 'topic');
        if (topicNode) {
          this.selectedTopic = topicNode.name;
        }

        // Expand all parent nodes to make the selected subtopic visible
        parentPath.forEach(parent => {
          this.topicTreeControl.expand(parent);
        });
      }

      this.subTopicClick.emit(node.name);

      // Log for debugging
      console.log(`Selected subtopic node: ${node.name}, emitting subTopicClick`);
    }

    // Now restore to allow other selections
    setTimeout(() => this.isSelecting = false, 100);

    this.updateNavigationPath();
  }

  // Find all parent nodes of a subtopic in the tree
  private findParentNodesPath(nodeName: string): TopicNode[] {
    const result: TopicNode[] = [];

    // Recursive helper function to search through the tree
    const findParents = (nodes: TopicNode[], targetName: string, currentPath: TopicNode[] = []): boolean => {
      for (const node of nodes) {
        // Create a new path for this branch
        const newPath = [...currentPath, node];

        // Check if this is the node we're looking for
        if (node.name === targetName) {
          // We found the node, add the current path (excluding the target node) to result
          result.push(...currentPath);
          return true;
        }

        // Search in children if they exist
        if (node.children && node.children.length > 0) {
          if (findParents(node.children, targetName, newPath)) {
            return true;
          }
        }
      }

      return false;
    };

    // Start the search from the root of the tree
    findParents(this.topicTreeData, nodeName);

    return result;
  }

  // Public method to check if a node is a parent of the selected subtopic
  isParentOfSelectedSubtopic(nodeName: string): boolean {
    if (!this.selectedSubTopic) return false;

    const parentNodes = this.findParentNodesPath(this.selectedSubTopic);
    return parentNodes.some(node => node.name === nodeName);
  }

  // Helper method to find and select all parent nodes of a subtopic
  private selectParentNodes(subtopicName: string): void {
    // Find the parent topic/subtopic path
    const parentPath = this.findParentNodesPath(subtopicName);

    if (parentPath.length > 0) {
      // The last element in the path should be a topic (top-level node)
      // Find the top-level topic node (if any)
      const topicNode = parentPath.find(node => node.type === 'topic');

      if (topicNode) {
        this.selectedTopic = topicNode.name;
        console.log(`Setting parent topic: ${topicNode.name}`);

        // Expand all parent nodes to make the selected subtopic visible
        parentPath.forEach(node => {
          this.topicTreeControl.expand(node);
        });
      }
    }
  }

  // Get all parent nodes in order from topic to the deepest parent subtopic before selected subtopic
  getOrderedParentSubtopics(): TopicNode[] {
    if (!this.selectedSubTopic) return [];

    const parentNodes = this.findParentNodesPath(this.selectedSubTopic);

    // Filter out the topic node since it's already displayed separately
    const subtopicParents = parentNodes.filter(node => node.type === 'subtopic');

    // Sort by level to ensure proper order (if level is available)
    return subtopicParents.sort((a, b) => {
      // If levels are available, sort by level
      if (a.level !== undefined && b.level !== undefined) {
        return a.level - b.level;
      }

      // Otherwise use the array order which should already be in hierarchy order
      return 0;
    });
  }

  // Handle both toggle and API call for topic nodes
  onTopicToggleClick(event: Event, node: TopicNode): void {
    // Stop propagation to prevent double handling
    event.stopPropagation();

    console.log(`Topic tree toggle clicked for node: ${node.name}, level: ${node.level}, hasChildren: ${node.hasChildren}`);

    // If we're expanding a node that has the hasChildren flag but no visible children yet,
    // explicitly try to load its children
    if (!this.topicTreeControl.isExpanded(node) && node.hasChildren && (!node.children || node.children.length === 0)) {
      console.log(`Node ${node.name} has children flag but no visible children. Attempting to load children...`);

      // For nodes deeper than subtopics (level > 4), we need to explicitly load their children
      if (node.level && node.level >= 4 && node.topicId) {
        console.log(`Loading deeper children for ${node.name} (level ${node.level}) with ID ${node.topicId}`);
        this.loadDeeperLevelNodes(node);
      }
    }

    // Toggle the node in the tree
    this.topicTreeControl.toggle(node);

    // Also perform the node selection to trigger API calls
    if (node.type === 'topic') {
      this.selectedTopic = node.name;
      this.selectedSubTopic = null;

      // Always emit topic click when toggle is clicked on a topic
      this.topicClick.emit(node.name);

      // If expanding and no children are loaded yet, force the API call
      if (this.topicTreeControl.isExpanded(node) && (!node.children || node.children.length === 0)) {
        console.log(`Forcing topic click API call for ${node.name}`);
        this.topicClick.emit(node.name);
      }
    } else {
      // For subtopic nodes
      this.selectedSubTopic = node.name;
      this.subTopicClick.emit(node.name);

      // If expanding and no children are loaded yet but has the children flag, force the API call
      if (this.topicTreeControl.isExpanded(node) && node.hasChildren && (!node.children || node.children.length === 0)) {
        this.subTopicClick.emit(node.name);
      }
    }
  }

  // Set the navigation mode
  setMode(val: 'topicwise' | 'yearwise'): void {
    this.p1Mode = val;

    // If switching to yearwise mode
    if (val === 'yearwise') {
      // If we don't have data, fetch it
      if (this.p1YearwiseExams.length === 0) {
        this.fetchYearwiseData();
      } else {
        // If we already have data, make sure tree is expanded
        this.updateYearwiseTreeData();
        this.expandAllYearwiseNodes();

        // Auto-select first items if nothing is selected
        if (!this.selectedYearwiseExam && this.p1YearwiseExams.length > 0) {
          const firstExam = this.p1YearwiseExams[0];
          this.onYearwiseExamClick(firstExam);
        }
      }
    }

    this.modeChange.emit(val);
  }

  // Toggle expand/collapse all nodes
  toggleAllNodes(): void {
    console.log('toggleAllNodes called, current state:', this.isTreeExpanded);
    console.log('Tree data count:', this.treeData.length);
    console.log('treeControl.dataNodes count:', this.treeControl.dataNodes?.length || 0);

    if (this.isTreeExpanded) {
      this.collapseAllNodes();
      this.isTreeExpanded = false;
    } else {
      this.expandAllNodes();
      this.isTreeExpanded = true;
    }

    // Force UI update
    this.changeDetectorRef.detectChanges();
  }

  // Expand all nodes in the main tree
  expandAllNodes(): void {
    // First check if dataNodes exists to prevent "Cannot read properties of undefined" error
    if (!this.treeControl.dataNodes || this.treeControl.dataNodes.length === 0) {
      console.log('Initializing treeControl.dataNodes in expandAllNodes');
      this.treeControl.dataNodes = this.treeData;
    }

    // Now safely expand all parent nodes and their children recursively
    this.treeControl.dataNodes.forEach(node => {
      this.treeControl.expand(node);
      if (node.children && node.children.length > 0) {
        // Expand all children as well, recursively
        this.expandNodeAndChildren(this.treeControl, node);
      }
    });

    console.log('Expanded all nodes in main tree');
    this.isTreeExpanded = true;

    // Force UI refresh
    this.changeDetectorRef.detectChanges();
  }

  // Helper function to recursively expand a node and all its children
  private expandNodeAndChildren<T>(treeControl: NestedTreeControl<T>, node: T): void {
    if (!node) return;

    try {
      // First expand this node
      treeControl.expand(node);

      // Get all descendants
      const children = treeControl.getDescendants(node);

      // If there are descendants, expand them too
      if (children && children.length > 0) {
        // For each child, expand it directly
        children.forEach(child => {
          if (child) {
            treeControl.expand(child);
          }
        });
      }
    } catch (error) {
      console.error('Error expanding node:', error);
    }
  }

  // Collapse all nodes in the main tree
  collapseAllNodes(): void {
    // Check if dataNodes exists to prevent "Cannot read properties of undefined" error
    if (!this.treeControl.dataNodes || this.treeControl.dataNodes.length === 0) {
      console.log('Initializing treeControl.dataNodes in collapseAllNodes');
      this.treeControl.dataNodes = this.treeData;
    }

    this.treeControl.dataNodes.forEach(node => {
      this.treeControl.collapse(node);
    });

    // Force UI refresh
    this.changeDetectorRef.detectChanges();
  }

  // Toggle expand/collapse all yearwise nodes
  toggleAllYearwiseNodes(): void {
    if (this.isYearwiseTreeExpanded) {
      this.collapseAllYearwiseNodes();
      this.isYearwiseTreeExpanded = false;
    } else {
      this.expandAllYearwiseNodes();
      this.isYearwiseTreeExpanded = true;
    }
  }

  // Expand all nodes in the yearwise tree
  expandAllYearwiseNodes(): void {
    // Check if dataNodes exists to prevent "Cannot read properties of undefined" error
    if (!this.yearwiseTreeControl.dataNodes || this.yearwiseTreeControl.dataNodes.length === 0) {
      return;
    }

    // Set expanded flag to true
    this.isYearwiseTreeExpanded = true;

    // Expand all parent nodes and their children recursively
    this.yearwiseTreeControl.dataNodes.forEach(node => {
      this.yearwiseTreeControl.expand(node);
      if (node.children && node.children.length > 0) {
        // Expand all children as well, recursively
        this.expandNodeAndChildren(this.yearwiseTreeControl, node);
      }
    });

    // Force a refresh of the tree view
    setTimeout(() => {
      this.yearwiseDataSource.data = [...this.yearwiseTreeData];
      this.changeDetectorRef.detectChanges();
    }, 50);
  }

  // Collapse all nodes in the yearwise tree
  collapseAllYearwiseNodes(): void {
    // Check if dataNodes exists to prevent "Cannot read properties of undefined" error
    if (!this.yearwiseTreeControl.dataNodes || this.yearwiseTreeControl.dataNodes.length === 0) {
      return;
    }

    this.yearwiseTreeControl.dataNodes.forEach(node => {
      this.yearwiseTreeControl.collapse(node);
    });
  }

  // Toggle expand/collapse all topic nodes
  toggleAllTopicNodes(): void {
    if (this.isTopicTreeExpanded) {
      this.collapseAllTopicNodes();
      this.isTopicTreeExpanded = false;
    } else {
      this.expandAllTopicNodes();
      this.isTopicTreeExpanded = true;
    }
  }

  // Expand all nodes in the topic tree
  expandAllTopicNodes(): void {
    // Check if dataNodes exists to prevent "Cannot read properties of undefined" error
    if (!this.topicTreeControl.dataNodes || this.topicTreeControl.dataNodes.length === 0) {
      return;
    }

    // Expand all parent nodes and their children recursively
    this.topicTreeControl.dataNodes.forEach(node => {
      this.topicTreeControl.expand(node);
      if (node.children && node.children.length > 0) {
        // Expand all children as well, recursively
        this.expandNodeAndChildren(this.topicTreeControl, node);
      }
    });

    console.log('Expanded all nodes in topic tree');
    this.isTopicTreeExpanded = true;
  }

  // Collapse all nodes in the topic tree
  collapseAllTopicNodes(): void {
    // Check if dataNodes exists to prevent "Cannot read properties of undefined" error
    if (!this.topicTreeControl.dataNodes || this.topicTreeControl.dataNodes.length === 0) {
      return;
    }

    this.topicTreeControl.dataNodes.forEach(node => {
      this.topicTreeControl.collapse(node);
    });
  }

  // Refresh the tree UI
  private refreshTreeUI(): void {
    // UI-only refresh: keep this local and avoid parent-triggered API refreshes.
    this.updateTreeData();
    this.updateYearwiseTreeData();
    this.updateTopicTreeData();
    this.ensureTreeControlsHaveData();
    this.changeDetectorRef.detectChanges();
  }

  // Restore specific expansions in main tree
  private restoreSpecificMainTreeExpansions(expandedSubjects: Set<string>): void {
    if (!this.treeControl.dataNodes) return;

    this.treeControl.dataNodes.forEach(node => {
      if (expandedSubjects.has(node.name)) {
        this.treeControl.expand(node);
      }

      // Always expand the selected subject
      if (node.name === this.selectedSubject) {
        this.treeControl.expand(node);
      }
    });
  }

  // Add method to handle sections loaded from parent
  onSectionsLoadedFromParent(data: {subject: string; sections: string[]}): void {
    console.log(`Sections loaded from parent for subject ${data.subject}:`, data.sections);

    // Update p1SubMap with the sections data
    if (data && data.subject && data.sections) {
      this.p1SubMap[data.subject] = data.sections;

      // Force update of the tree
      this.updateTreeData();

      // If selected subject matches the one that sections were loaded for, expand it
      if (this.selectedSubject === data.subject) {
        setTimeout(() => {
          const subjectNode = this.treeControl.dataNodes?.find(node => node.name === data.subject);
          if (subjectNode) {
            this.treeControl.expand(subjectNode);
          }
        }, 100);
      }
    }
  }

  // Add method to handle topics loaded from parent
  onTopicsLoadedFromParent(data: {section: string; topics: string[]}): void {
    console.log(`Topics loaded from parent for section ${data.section}:`, data.topics);

    // Update p2TopicsMap with the topics data
    if (data && data.section && data.topics) {
      this.p2TopicsMap[data.section] = data.topics;

      // If the currently selected section matches the one topics were loaded for,
      // update the topic tree
      if (this.selectedSubItem === data.section) {
        this.updateTopicTreeData();
      }
    }
  }

  // Add method to handle subtopics loaded from parent
  onSubtopicsLoadedFromParent(data: {topic: string; subtopics: string[]}): void {
    console.log(`Subtopics loaded from parent for topic ${data.topic}:`, data.subtopics);

    // Update p2SubTopicsMap with the subtopics data
    if (data && data.topic && data.subtopics) {
      this.p2SubTopicsMap[data.topic] = data.subtopics;

      // If the currently selected topic matches the one subtopics were loaded for,
      // update the topic tree
      if (this.selectedTopic === data.topic) {
        this.updateTopicTreeData();

        // Find and expand the selected topic node
        setTimeout(() => {
          const topicNode = this.topicTreeControl.dataNodes?.find(node => node.name === data.topic);
          if (topicNode) {
            this.topicTreeControl.expand(topicNode);
          }
        }, 100);
      }
    }
  }

  // Add method to handle language change
  onLanguageChange(language: string, event?: Event): void {
    // Prevent event propagation if event is provided
    if (event) {
      event.stopPropagation();
    }

    console.log(`Language changed to ${language}`);

    // Update the selected language
    this.selectedLanguage = language;

    // Emit the language change event
    this.languageChange.emit(language);

    // Force refresh of the tree UI to apply language changes
    setTimeout(() => {
      this.refreshTreeUI();
    }, 100);
  }

  /**
   * Add a new subject at the root level (parent_topic_id = 0, item_level = 1)
   */
  addSubject(): void {
    console.log('Adding a new subject (root level)');
    
    // Open dialog to get new subject details
    const dialogRef = this.dialog.open(TopicDialogComponent, {
      width: '500px',
      data: {
        parentTopicId: '0', // Root level
        parentName: 'Root',
        parentLevel: 0,      // Root level (subjects are level 1)
        isSubject: true      // Flag to indicate this is a top-level subject
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Create API payload for new subject
        const payload = {
          name_eng: result.nameEng,
          name_hindi: result.nameHindi || undefined,
          parent_topic_id: '0',    // Root level parent
          item_level: 1            // Level 1 (subject)
        };

        // Call API to create the subject
        this.treeEditService.createTopic(payload).subscribe({
          next: (response) => {
            console.log('Subject created successfully:', response);
            
            // Refresh the tree to show the new subject with complete data
            this.refreshTopicTree();
            
            // Show success message (optional)
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: {
                title: 'Success',
                message: `Subject "${result.nameEng}" created successfully`,
                confirmText: 'OK',
                showCancel: false
              }
            });
          },
          error: (error) => {
            console.error('Error creating subject:', error);
            
            // Show error message
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: {
                title: 'Error',
                message: `Failed to create subject: ${error.error?.message || 'Unknown error'}`,
                confirmText: 'OK',
                showCancel: false
              }
            });
          }
        });
      }
    });
  }
}
